import React, { useState, useMemo, useEffect } from "react";
import UserSidebar from "./UserSidebar";
import UserHeader from "./UserHeader";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api_call";
import { FaSearch, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { IoIosInformationCircle } from "react-icons/io";
import "./Dashboard.css";

export default function ComplianceList() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
  key: "complianceId",
  direction: "asc",
});

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAct, setSelectedAct] = useState("Statutory Compliance");
  const [selectedCountry, setSelectedCountry] = useState("");

  const navigate = useNavigate();

  const formatDate = (val) => {
    if (!val) return "";
    const d = new Date(val);
    return isNaN(d) ? "" : d.toISOString().split("T")[0];
  };

  // ---------------- FETCH DATA ----------------
  useEffect(() => {
    const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
    if (!isLoggedIn) {
      navigate("/", { replace: true });
      return;
    }

    const act = localStorage.getItem("selectedAct");
    const country = localStorage.getItem("selectedCountry");

    if (act) setSelectedAct(act);
    if (country) setSelectedCountry(country);
    if (!act || !country) return;

    const fetchComplianceData = async () => {
  try {
    setLoading(true);
    setError(null);

    const res = await apiFetch(
      `/compliance/filter?country=${country}&act=${act}`
    );

    const records = Array.isArray(res?.records) ? res.records : [];

    const formatted = records.map((r) => ({
      complianceId: String(r.cmplst_compliance_key),
      act: act,
      particular: r.cmplst_particular || "",
      description: r.cmplst_description || "",
      longDescription: r.cmplst_long_description || "",
      startDate: r.cmplst_start_date || "",
      actionDate: r.cmplst_action_date || "",
      endDate: r.cmplst_end_date || "",
    }));

    setData(formatted);
  } catch (err) {
    console.error("API error:", err);
    setError("Failed to load compliance data.");
    setData([]);
  } finally {
    setLoading(false);
  }

    };

    fetchComplianceData();
  }, [navigate]);

  // ---------------- ADD ----------------
  const handleAddClick = (row) => {
    localStorage.setItem("selectedComplianceRow", JSON.stringify(row));
    navigate("/add_compliance");
  };

  // ---------------- SEARCH ----------------
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(
      (item) =>
        item.complianceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.act.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.particular.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [data, searchTerm]);

  // ---------------- SORT ----------------
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="sort-icon" />;
    return sortConfig.direction === "asc" ? <FaSortUp className="sort-icon" /> : <FaSortDown className="sort-icon" />;
  };

  const sortedData = useMemo(() => {
    let sortable = [...filteredData];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const A = String(a[sortConfig.key]).toLowerCase();
        const B = String(b[sortConfig.key]).toLowerCase();
        if (A < B) return sortConfig.direction === "asc" ? -1 : 1;
        if (A > B) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [filteredData, sortConfig]);

  // ---------------- UI ----------------
  return (
    <div className="StatutoryInfo">
      <UserSidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <UserHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />

      <div className="headSection">
        <div className="compliance-score">{selectedAct}</div>
      </div>

      <div className="table-header">
        <div></div>
        <div className="table-actions">
          <div className="search-box">
            <input placeholder="Search Compliance..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            <FaSearch className="search-icon" />
          </div>
        </div>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th onClick={() => requestSort("complianceId")}>Compliance ID {getSortIcon("complianceId")}</th>
            <th onClick={() => requestSort("act")}>Act {getSortIcon("act")}</th>
            <th onClick={() => requestSort("particular")}>Particular {getSortIcon("particular")}</th>
            <th>Action</th>
          </tr>
        </thead>

       <tbody>
  {loading ? (
    <tr>
      <td colSpan="4" style={{ textAlign: "center", padding: "15px" }}>
        Loading...
      </td>
    </tr>
  ) : error ? (
    <tr>
      <td colSpan="4" style={{ textAlign: "center", padding: "15px", color: "red" }}>
        {error}
      </td>
    </tr>
  ) : sortedData.length === 0 ? (
    <tr>
      <td colSpan="4" style={{ textAlign: "center", padding: "15px" }}>
        No records found
      </td>
    </tr>
  ) : (
    sortedData.map((item, index) => (
      <tr key={index}>
        <td>{item.complianceId}</td>
        <td>{item.act}</td>

        <td>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span>{item.particular}</span>

            {item.description && (
              <IoIosInformationCircle
                title={item.description.replace(/^return:\s*/i, "")}
                style={{
                  cursor: "pointer",
                  fontSize: "16px",
                  color: "#1A1744",
                }}
              />
            )}
          </div>
        </td>

        <td>
          <button
            className="action-btn primary"
            onClick={() => handleAddClick(item)}
          >
            Add
          </button>
        </td>
      </tr>
    ))
  )}
</tbody>
      </table>
    </div>
  );
}
